import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle, XCircle, CircleNotch } from "@phosphor-icons/react";
import { useApi } from "@/lib/api";
import { analytics, analyticsConsent } from "@/lib/heycatch";

export default function PaymentResult() {
  const api = useApi();
  const { result } = useParams();
  const [params] = useSearchParams();
  const qc = useQueryClient();
  const [status, setStatus] = useState(result === "success" ? "checking" : "cancelled");

  useEffect(() => {
    const sid = params.get("session_id");
    if (result !== "success" || !sid) return;
    let n = 0, stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const r = await api.get(`/payments/status/${sid}`);
        if (r.payment_status === "paid") {
          setStatus("paid"); qc.invalidateQueries({ queryKey: ["me"] });
          if (analyticsConsent() && localStorage.getItem("cr_hc_sub") !== sid) { localStorage.setItem("cr_hc_sub", sid); analytics.trackEvent("subscription_started", { plan: r.plan }); }
          return;
        }
        if (["expired", "failed"].includes(r.payment_status)) return setStatus("failed");
      } catch {}
      if (++n < 15) setTimeout(poll, 2000); else setStatus("timeout");
    };
    poll();
    return () => { stop = true; };
  }, [result, params, qc, api]);

  const view = {
    checking: [CircleNotch, "#A3A3A3", "Confirming your subscription…", "animate-spin"],
    paid: [CheckCircle, "#00FF9D", "You're in. Trial started."],
    cancelled: [XCircle, "#A3A3A3", "Checkout cancelled. No charge made."],
    failed: [XCircle, "#FF5C7A", "Payment didn't go through."],
    timeout: [CircleNotch, "#A3A3A3", "Still processing — check Billing in a minute."],
  }[status];
  const [Icon, color, text, cls = ""] = view;

  return (
    <div className="min-h-screen grid place-items-center bg-[#050505] p-6">
      <div data-testid="payment-result" className="rounded-2xl border border-[#2A2A2A] bg-[#121212] p-10 text-center max-w-md w-full">
        <Icon size={48} style={{ color }} className={`mx-auto ${cls}`} />
        <p data-testid="payment-status-text" className="font-display text-2xl mt-5">{text}</p>
        <Link to="/app" data-testid="back-to-dashboard-btn" className="inline-block mt-8 rounded-full bg-[#00FF9D] px-6 py-3 font-semibold text-[#050505]">Back to dashboard</Link>
      </div>
    </div>
  );
}
